import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getClientById, subscribeToClients, subscribeToObjectsByClient, getObjectsByClient, isProfileComplete, isObjectIncomplete } from '../utils/storage';
import { getPermissions } from '../utils/permissions';
import AccessDeniedModal from './AccessDeniedModal';
import CreateObjectModal from './CreateObjectModal';
import EditClientModal from './EditClientModal';
import DuplicateObjectModal from './DuplicateObjectModal';
import LoadingScreen from './LoadingScreen';
import { SearchBar, EmptyState } from './templates';
import './ClientDetail.css';

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const perms = getPermissions(userProfile?.roles || []);
  const [client, setClient] = useState(null);
  const [objects, setObjects] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showObjectModal, setShowObjectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddObjDenied, setShowAddObjDenied] = useState(false);
  const [search, setSearch] = useState('');
  const [pageReady, setPageReady] = useState(false);
  // Object the user clicked the "duplicate" icon on. The
  // DuplicateObjectModal walks them through the 3-step flow and then
  // performs the actual cloning.
  const [dupTarget, setDupTarget] = useState(null);

  // Real-time listener for all clients (for chevron navigation)
  useEffect(() => {
    const unsubscribe = subscribeToClients((clientList) => {
      setAllClients(clientList);
      // Also update current client from the list
      const current = clientList.find((c) => c.id === id);
      if (current) {
        setClient(current);
      }
    });
    return () => unsubscribe();
  }, [id]);

  // Load current client on mount / id change
  useEffect(() => {
    async function load() {
      const c = await getClientById(id);
      if (!c) {
        navigate('/test-sites');
        return;
      }
      setClient(c);
      setLoading(false);
    }
    load();
  }, [id, navigate]);

  // Real-time listener for objects of this client
  useEffect(() => {
    const unsubscribe = subscribeToObjectsByClient(id, (objectList) => {
      setObjects(objectList);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [id]);

  function handleAddObjectClick() {
    if (perms.canAddObject) {
      setShowObjectModal(true);
    } else {
      setShowAddObjDenied(true);
    }
  }

  // One-shot refresh that bypasses the 15s subscribeToObjectsByClient
  // poll tick — used right after a mutation (create / duplicate) so
  // the new rows appear without the user waiting up to 15 seconds.
  async function refreshObjects() {
    try {
      const fresh = await getObjectsByClient(id);
      setObjects(fresh);
    } catch (err) {
      // Non-fatal — the poller will catch up on its next tick.
      console.error('refreshObjects failed:', err);
    }
  }

  function handleObjectCreated() {
    setShowObjectModal(false);
    refreshObjects();
  }

  async function refreshClient() {
    try {
      const c = await getClientById(id);
      if (c) setClient(c);
    } catch (err) {
      console.error('Failed to refresh client:', err);
    }
  }

  function handleClientUpdated() {
    setShowEditModal(false);
    refreshClient();
  }

  function handleClientDeleted() {
    navigate('/test-sites');
  }

  if (!pageReady) {
    return (
      <LoadingScreen
        isLoading={loading || !client}
        onDone={() => setPageReady(true)}
      />
    );
  }

  const complete = isProfileComplete(client);

  // Filter by search term (object tag or type)
  const filtered = search.trim()
    ? objects.filter((o) =>
        (o.objectTag || '').toLowerCase().includes(search.trim().toLowerCase()) ||
        (o.type     || '').toLowerCase().includes(search.trim().toLowerCase())
      )
    : objects;

  // Sort by generalInfo.startDate ascending; objects without a date go to the bottom
  const sortedObjects = [...filtered].sort((a, b) => {
    const da = a.generalInfo?.startDate || '';
    const db = b.generalInfo?.startDate || '';
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da.localeCompare(db);
  });

  // Chevron navigation: sorted client list
  const sortedClients = [...allClients].sort((a, b) => a.name.localeCompare(b.name));
  const currentIndex = sortedClients.findIndex((c) => c.id === id);
  const prevClient = currentIndex > 0 ? sortedClients[currentIndex - 1] : null;
  const nextClient = currentIndex < sortedClients.length - 1 ? sortedClients[currentIndex + 1] : null;
  const showChevrons = sortedClients.length > 1;

  return (
    <div className="page-container">
      <button className="btn-back" onClick={() => navigate('/test-sites')}>
        &larr; Back to Test Sites
      </button>

      <header className="detail-header">
        <div className="detail-header-top">
          <h1>{client.name}</h1>
          <div className="header-actions">
            {showChevrons && (
              <div className="nav-chevrons">
                {prevClient ? (
                  <button className="nav-chevron" onClick={() => navigate(`/client/${prevClient.id}`)} title={prevClient.name}>&lsaquo;</button>
                ) : <span className="nav-chevron-hidden">&lsaquo;</span>}
                {nextClient ? (
                  <button className="nav-chevron" onClick={() => navigate(`/client/${nextClient.id}`)} title={nextClient.name}>&rsaquo;</button>
                ) : <span className="nav-chevron-hidden">&rsaquo;</span>}
              </div>
            )}
          </div>
        </div>
        <div className="detail-info">
          <div className="od-info-header-row">
            <div className="od-info-title" style={{ marginBottom: 0 }}>Test Site Information</div>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowEditModal(true)}>
              Edit
            </button>
          </div>
          <p className="detail-info-client">{client.clientName || '—'}</p>
          <p>Address: {[client.addressLine, client.postCode, client.city, client.country].filter(Boolean).join(', ') || '—'}</p>
          <p>Contact Person: {client.contactPerson || '—'}</p>
        </div>
        {!complete && (() => {
          const missing = [];
          if (!client.contactPerson) missing.push('Contact Person');
          if (!client.addressLine)   missing.push('Address');
          if (!client.postCode)      missing.push('Post Code');
          if (!client.country)       missing.push('Country');
          return (
            <div className="insp-incomplete-alert">
              <span className="insp-incomplete-icon">&#9888;</span>
              Incomplete &mdash; {missing.join(', ')}
            </div>
          );
        })()}
      </header>

      <div className="toolbar">
        {objects.length > 0 && (
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search objects…"
          />
        )}
        <button className="btn btn-primary" onClick={handleAddObjectClick}>
          + Add Object
        </button>
      </div>

      {objects.length === 0 ? (
        <EmptyState
          icon="📦"
          title="No objects yet"
          description="Create the first object for this test site."
        >
          <button className="btn btn-primary" onClick={handleAddObjectClick}>
            + Add Object
          </button>
        </EmptyState>
      ) : (
        <div className="table-wrapper">
          <table className="ds-table ds-table-fixed cd-objects-table">
            <colgroup>
              <col className="cd-col-date" />
              <col className="cd-col-id" />
              <col className="cd-col-type" />
              <col className="cd-col-arrow" />
            </colgroup>
            <thead>
              <tr>
                <th>Start Date</th>
                <th>Object ID ({sortedObjects.length})</th>
                <th>Type</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedObjects.map((obj) => (
                <tr key={obj.id} className="site-row" onClick={() => navigate(`/client/${id}/object/${obj.id}`)}>
                  <td className="cd-start-date">
                    {fmtDate(obj.generalInfo?.startDate || '')}
                  </td>
                  <td>
                    <div className="obj-id-cell">
                      <span className="obj-tag">{obj.objectTag || '—'}</span>
                      {isObjectIncomplete(obj) && (
                        <span className="obj-incomplete-chip">INCOMPLETE</span>
                      )}
                    </div>
                  </td>
                  <td className="obj-type">{obj.type || '—'}</td>
                  <td className="site-actions">
                    <button
                      type="button"
                      className="site-dup-btn"
                      title="Duplicate this object"
                      aria-label="Duplicate object"
                      onClick={(e) => { e.stopPropagation(); setDupTarget(obj); }}
                    >
                      {/* Two-rectangles "copy" icon */}
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="11" height="11" rx="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </button>
                    <span className="site-arrow">&rsaquo;</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showObjectModal && (
        <CreateObjectModal
          clientId={id}
          onClose={() => setShowObjectModal(false)}
          onCreated={handleObjectCreated}
        />
      )}

      {showEditModal && (
        <EditClientModal
          client={client}
          onClose={() => setShowEditModal(false)}
          onUpdated={handleClientUpdated}
          onDeleted={handleClientDeleted}
        />
      )}

      {showAddObjDenied && (
        <AccessDeniedModal
          roles={userProfile?.roles || []}
          onClose={() => setShowAddObjDenied(false)}
        />
      )}

      {dupTarget && (
        <DuplicateObjectModal
          clientId={id}
          object={dupTarget}
          onClose={() => setDupTarget(null)}
          onDone={() => {
            // Loading screen finishes → instantly pull the fresh list
            // so the duplicates appear without the 15s poll wait.
            setDupTarget(null);
            refreshObjects();
          }}
        />
      )}
    </div>
  );
}
